import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';

import { Router } from '@angular/router';
import { AdminService } from '../../../../services/admin.service';
import { AlertsService } from '../../../../../services/alerts/alerts.service';
import { AlertsType } from '@rappi/ui/alerts';
import { select, Store } from '@ngrx/store';
import { StoreName } from '../../../../../store/definitions/store.constants';
import { AppState } from '../../../../../store/states/app.state';

import { FormControl, FormGroup, Validators } from '@angular/forms';

import { Observable, Subject } from 'rxjs';
import { skip, takeUntil, tap } from 'rxjs/operators';
import { Permission } from '../../../../definitions/permissions.models';
import { PermissionAction } from '../../../../definitions/permissions.enums';
import { Admin } from '../../../../definitions/admin.models';

@Component({
  selector: 'app-manager',
  templateUrl: './manager.component.html',
  styleUrls: ['./manager.component.scss']
})
export class ManagerComponent implements OnInit, OnDestroy {
  action: string;
  form: FormGroup = new FormGroup({
    email: new FormControl(null, Validators.compose([Validators.required, Validators.email]))
  });

  id: number;

  deleteAdmin: (id: number) => Observable<Permission<unknown>>;

  viewUrl: string;
  tableUrl: string;

  destroySubject$: Subject<void> = new Subject();

  private readonly ADMIN_CREATION_MESSAGE = 'Admin created successfully';
  private readonly ADMIN_CREATION_ERROR = 'There was an error trying to create admin';

  private readonly ADMIN_UPDATE_MESSAGE = 'Admin updated successfully';
  private readonly ADMIN_UPDATE_ERROR = 'There was an error trying to update admin with id';

  constructor(
    private readonly _cd: ChangeDetectorRef,
    private readonly _router: Router,
    private readonly _adminService: AdminService,
    private readonly _alertsService: AlertsService,
    private readonly _store: Store<AppState>
  ) {
    const routeArr = this._router.url.split('/');

    this.id = Number(routeArr[4]);
    this.action = routeArr[3];

    routeArr.length = 3;

    this.tableUrl = routeArr.join('/');
    this.viewUrl = this.id ? `${this.tableUrl}/${PermissionAction.view}/${this.id}` : null;
    this.deleteAdmin = (id: number) => this._adminService.deleteAdmin(id).pipe(tap(() => this.goTo(this.tableUrl)));

    this._store
      .pipe(
        select(StoreName.country),
        skip(1),
        takeUntil(this.destroySubject$),
        tap(() => this.goTo(this.tableUrl))
      )
      .subscribe();
  }

  ngOnInit() {
    if (this.id) {
      this._adminService.getAdmin(this.id).subscribe(
        ({ data }: Permission<Admin>) => {
          this.form.patchValue(data);

          this._cd.detectChanges();
        },
        ({ error: { message } }: HttpErrorResponse) => {
          this._alertsService.openAlerts(message || 'There was an error trying to get admin', AlertsType.error);
        }
      );
    }
  }

  goTo(url: string) {
    this._router.navigateByUrl(url);
  }

  onSubmit(f: FormGroup) {
    if (f.invalid) {
      f.markAllAsTouched();
      return;
    }

    const request = f.getRawValue() as Admin;

    const action = this.id ? this._adminService.editAdmin(request, this.id) : this._adminService.createAdmin(request);

    const MESSAGE: string = this.id ? this.ADMIN_UPDATE_MESSAGE : this.ADMIN_CREATION_MESSAGE;
    const ERROR: string = this.id ? `${this.ADMIN_UPDATE_ERROR} ${this.id}` : this.ADMIN_CREATION_ERROR;

    action.subscribe(
      ({ data: { id } }: Permission<Admin>) => {
        this._alertsService.openAlerts(MESSAGE, AlertsType.success);
        this.goTo(this.viewUrl || `${this.tableUrl}/${PermissionAction.view}/${id}`);
      },
      ({ error: { message } }: HttpErrorResponse) => {
        this._alertsService.openAlerts(message || ERROR, AlertsType.error);
      }
    );
  }

  ngOnDestroy() {
    this.destroySubject$.next();
    this.destroySubject$.complete();
  }
}
